import {
    getIdvSteps,
    getNextPosition,
    getPrevPosition,
} from '@/components/idv/helpers';
import { IdvModalProps } from '@/components/idv/IdvModal';
import { useIdvPosition } from '@/components/idv/useIdvPosition';
import {
    IDV_POSITIONS,
    IdvPosition,
} from '@common/react/idv/useBaseIdvPosition';
import { useCurrentUser } from '@common/react/user';
import { OnboardingData } from '@common/types';
import React, { useCallback, useContext, useMemo, useState } from 'react';

interface IdvContext extends IdvModalProps {
    steps: IdvPosition[];
    position: IdvPosition;
    setPosition: (position: IdvPosition) => void;
    onNext: (onboardingData?: OnboardingData) => void;
    onBack: () => void;
}

const IdvContext = React.createContext<IdvContext>({
    steps: IDV_POSITIONS,
    position: 'profile',
    setPosition: () => false,
    onNext: () => false,
    onBack: () => false,
    onComplete: () => false,
    includeInviteStep: false,
    title: 'individual',
    subtitle: 'individual',
    consentTitle: '',
    consentSubtitle: '',
});

interface IdvProviderProps extends IdvModalProps {
    children: React.ReactNode;
}

export const IdvProvider = ({
    children,
    onComplete,
    includeInviteStep,
    title,
    subtitle,
    consentTitle,
    consentSubtitle,
}: IdvProviderProps) => {
    const { userInfo } = useCurrentUser();
    const initialPosition = useIdvPosition();
    const [position, setPosition] = useState(initialPosition);

    const getStepsParams = useCallback(
        (onboardingData?: OnboardingData) => ({
            currentView: position,
            onboardingData: onboardingData || userInfo?.onboardingData,
        }),
        [position, userInfo?.onboardingData]
    );

    const steps = useMemo(
        () => getIdvSteps(getStepsParams()),
        [getStepsParams]
    );

    const onNext = (onboardingData?: OnboardingData) => {
        const nextPosition = getNextPosition(getStepsParams(onboardingData));
        setPosition(nextPosition);
    };

    const onBack = () => {
        const prevPosition = getPrevPosition(getStepsParams());
        setPosition(prevPosition);
    };

    return (
        <IdvContext.Provider
            value={{
                steps,
                position,
                setPosition,
                onNext,
                onBack,
                onComplete,
                includeInviteStep,
                title,
                subtitle,
                consentTitle,
                consentSubtitle,
            }}
        >
            {children}
        </IdvContext.Provider>
    );
};

export const useIdvContext = () => useContext(IdvContext);
